import React from 'react';
import { Card, Collapse, Tag, Statistic, Row, Col } from 'antd';

const { Panel } = Collapse;

interface Partition {
    name: string;
    description: string;
    row_range: number[];
    col_range: number[];
    miss_count: number;
    hit_count: number;
    hit_rate: number;
    strategy_used: string;
}

interface QueryBlock {
    block_id: number;
    partition: string;
    strategy: string;
    origins: number[];
    destinations: number[];
    query_size: number;
    actual_missing: number;
    redundancy: number;
    redundancy_rate: number;
}

interface ReorderMapping {
    old_to_new: number[];
    new_to_old: number[];
}

interface QueryBlocksAnalysisProps {
    partitions: Partition[];
    queryBlocks: QueryBlock[];
    totalMissing: number;
    reorderMapping?: ReorderMapping;
}

const QueryBlocksAnalysis: React.FC<QueryBlocksAnalysisProps> = ({
    partitions,
    queryBlocks,
    totalMissing,
    reorderMapping
}) => {
    // 将重排后的索引转换回原始索引（从1开始）
    const mapToOriginalIndices = (reorderedIndices: number[]): number[] => {
        if (!reorderMapping || !reorderMapping.new_to_old) {
            return reorderedIndices.map(idx => idx + 1);
        }
        return reorderedIndices.map(idx => reorderMapping.new_to_old[idx] + 1);
    };
    const getStrategyName = (strategy: string) => {
        switch (strategy) {
            case 'skip':
                return '跳过';
            case 'point_to_point':
                return '点对点查询';
            case 'greedy_merge':
                return '贪心合并';
            case 'whole_block':
                return '整块覆盖';
            default:
                return strategy;
        }
    };

    const getStrategyColor = (strategy: string) => {
        switch (strategy) {
            case 'skip':
                return 'default';
            case 'point_to_point':
                return 'blue';
            case 'greedy_merge':
                return 'orange';
            case 'whole_block':
                return 'purple';
            default:
                return 'default';
        }
    };

    const getStrategyEmoji = (strategy: string) => {
        switch (strategy) {
            case 'skip':
                return '✅';
            case 'point_to_point':
                return '🎯';
            case 'greedy_merge':
                return '🔗';
            case 'whole_block':
                return '📦';
            default:
                return '🎯';
        }
    };

    const totalQuerySize = queryBlocks.reduce((sum, block) => sum + block.query_size, 0);
    const totalRedundancy = queryBlocks.reduce((sum, block) => sum + block.redundancy, 0);
    const overallRedundancyRate = totalQuerySize > 0 ? totalRedundancy / totalQuerySize : 0;

    return (
        <Card title="📦 查询块分析">
            <div className="space-y-6">
                {/* 分区概览 */}
                {partitions.map((partition) => {
                    const partitionBlocks = queryBlocks.filter(
                        (block) => block.partition === partition.name
                    );

                    return (
                        <div key={partition.name} className="border rounded p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <span className="text-xl">{getStrategyEmoji(partition.strategy_used)}</span>
                                <h4 className="text-lg font-semibold mb-0">
                                    {partition.name} {partition.description}
                                </h4>
                                <Tag color={getStrategyColor(partition.strategy_used)}>
                                    {getStrategyName(partition.strategy_used)}
                                </Tag>
                            </div>

                            <div className="space-y-2 text-sm mb-3">
                                <div>
                                    <span className="text-gray-600">范围: </span>
                                    <span className="font-mono">
                                        行[{partition.row_range[0]}-{partition.row_range[1] - 1}] × 
                                        列[{partition.col_range[0]}-{partition.col_range[1] - 1}]
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-600">缺失: </span>
                                    <span className="font-semibold">{partition.miss_count}个</span>
                                    <span className="text-gray-600 ml-3">命中率: </span>
                                    <span className="font-semibold">{(partition.hit_rate * 100).toFixed(1)}%</span>
                                </div>
                            </div>

                            {/* 查询块详情 */}
                            {partitionBlocks.length > 0 && (
                                <Collapse size="small" className="bg-gray-50">
                                    {partitionBlocks.map((block) => (
                                        <Panel
                                            header={
                                            <div className="flex items-center gap-2">
                                                <span>查询块 #{block.block_id}</span>
                                                <Tag color={getStrategyColor(block.strategy)}>
                                                    {getStrategyName(block.strategy)}
                                                </Tag>
                                                <span className="text-gray-500 text-xs">
                                                    {block.query_size}个点
                                                </span>
                                            </div>
                                            }
                                            key={block.block_id}
                                        >
                                            <div className="space-y-2 text-sm">
                                                <div>
                                                    <span className="text-gray-600">起点(原始索引): </span>
                                                    <span className="font-mono">[{mapToOriginalIndices(block.origins).join(', ')}]</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">终点(原始索引): </span>
                                                    <span className="font-mono">[{mapToOriginalIndices(block.destinations).join(', ')}]</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">查询大小: </span>
                                                    <span className="font-semibold">{block.query_size}个点</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">实际缺失: </span>
                                                    <span className="font-semibold">{block.actual_missing}个</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">冗余: </span>
                                                    <span className="font-semibold text-orange-600">
                                                        {block.redundancy}个 ({(block.redundancy_rate * 100).toFixed(1)}%)
                                                    </span>
                                                </div>
                                            </div>
                                        </Panel>
                                    ))}
                                </Collapse>
                            )}

                            {partition.strategy_used === 'skip' && (
                                <div className="text-green-600 text-sm">
                                    ✅ 无缺失，跳过查询
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* 汇总统计 */}
                <div className="bg-blue-50 border border-blue-200 rounded p-4">
                    <h4 className="text-lg font-semibold mb-3">📊 查询块汇总</h4>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Statistic
                                title="总块数"
                                value={queryBlocks.length}
                                suffix="个"
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="查询点数"
                                value={totalQuerySize}
                                suffix="个"
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="实际缺失"
                                value={totalMissing}
                                suffix="个"
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="冗余率"
                                value={(overallRedundancyRate * 100).toFixed(1)}
                                suffix="%"
                                valueStyle={{ color: overallRedundancyRate < 0.2 ? '#52c41a' : '#faad14' }}
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </Card>
    );
};

export default QueryBlocksAnalysis;
